import { useEffect, useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { faqs } from '@/mocks/faqs';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9slvetlbrt1qcgaj5u0';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于MJHL - 品牌介绍、声誉与联系方式',
  description: '了解MJHL品牌故事、核心价值、行业声誉及联系方式。MJHL是您值得信赖的首选品牌。',
  url: 'https://mjhl.com/about',
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

const milestones = [
  { year: '2020', title: '品牌创立', description: 'MJHL 正式成立，以"科技创新驱动美好生活"为使命启航。' },
  { year: '2021', title: '产品首发', description: '推出首款智能家居产品，迅速获得市场认可和用户好评。' },
  { year: '2022', title: '业务拓展', description: '进军企业服务领域，推出 WorkFlow 协同平台，服务首批 100 家企业客户。' },
  { year: '2023', title: '高速成长', description: '业务扩展至健康科技和教育科技，全国建立 20+ 城市服务中心。' },
  { year: '2024', title: '生态构建', description: '发布"星云计划"生态战略，合作伙伴超 300 家，服务用户突破 50 万。' },
  { year: '2025', title: '全新启航', description: '荣获多项国际设计大奖和行业认证，持续引领行业创新发展。' },
];

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setSuccessMessage('感谢您的留言，我们会尽快与您联系！');
      form.reset();
      return;
    }
    if (!CONTACT_FORM_URL) {
      setFormStatus('error');
      setFormError('表单暂不可用，请稍后再试');
      return;
    }
    setFormStatus('loading');
    setFormError('');
    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          params.append(key, value as string);
        }
      });
      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (response.ok && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        setSuccessMessage('感谢您的留言，我们会尽快与您联系！');
        form.reset();
      } else {
        setFormStatus('error');
        const serverMsg =
          (parsed as { meta?: { message?: string } }).meta?.message ||
          (parsed as { message?: string }).message ||
          (parsed as { meta?: { detail?: string } }).meta?.detail ||
          responseText ||
          '提交失败，请稍后再试';
        setFormError(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
    setFormStatus('idle');
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <main className="min-h-screen bg-background-50">
        <div className="pt-20">
          <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />

          <section className="w-full px-4 md:px-6 py-10 md:py-16">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-950">
                  关于 <strong>MJHL</strong>
                </h1>
                <p className="mt-4 text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                  MJHL 成立于 2020 年，以"科技创新驱动美好生活"为使命，致力于为全球用户提供智能家居、企业服务、健康科技、教育科技等全方位的卓越产品与服务体验。我们相信科技的力量可以改变生活，让每个人都能享受更智慧、更便捷、更美好的未来。
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-eye-line text-2xl"></i>
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-foreground-950">我们的愿景</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                    成为全球领先的科技创新品牌，让每个人都能享受科技带来的美好生活。
                  </p>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className="ri-flag-line text-2xl"></i>
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-foreground-950">我们的使命</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                    以科技创新驱动美好生活，为用户提供卓越的产品与服务体验。
                  </p>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                    <i className="ri-heart-line text-2xl"></i>
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-foreground-950">核心价值观</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                    安全可靠、技术领先、用户至上、开放共赢，这是我们坚守的四大核心价值。
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="w-full py-12 md:py-18 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto">
                <h2 className="text-xl md:text-3xl font-bold text-foreground-950 text-center mb-10">
                  品牌发展历程
                </h2>
                <div className="relative">
                  <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px"></div>
                  <div className="flex flex-col gap-8">
                    {milestones.map((m, i) => (
                      <div key={m.year} className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                        <div className="md:w-1/2 pl-10 md:pl-0 md:px-8">
                          <div className={`md:text-right ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                            <span className="inline-block px-3 py-1 text-xs font-bold rounded-full bg-primary-500 text-white whitespace-nowrap">
                              {m.year}
                            </span>
                            <h3 className="mt-2 text-base font-semibold text-foreground-950">
                              {m.title}
                            </h3>
                            <p className="mt-1 text-sm text-foreground-600 leading-relaxed">
                              {m.description}
                            </p>
                          </div>
                        </div>
                        <div className="absolute left-3 md:left-1/2 top-2 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 md:-translate-x-1/2"></div>
                        <div className="md:w-1/2 hidden md:block"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-12 md:py-18 bg-background-50">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-xl md:text-3xl font-bold text-foreground-950 text-center mb-10">
                品牌荣誉与认证
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {[
                  { icon: 'ri-award-line', name: 'iF 设计大奖', year: '2025' },
                  { icon: 'ri-shield-check-line', name: '金融级安全认证', year: '2025' },
                  { icon: 'ri-verified-badge-line', name: 'ISO 27001 认证', year: '2024' },
                  { icon: 'ri-medal-line', name: '等保三级认证', year: '2024' },
                ].map((award, i) => (
                  <div key={i} className="bg-background-50 border border-background-200/70 rounded-lg p-5 text-center hover:border-primary-300 transition-all duration-300">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className={`${award.icon} text-xl`}></i>
                    </div>
                    <h4 className="mt-3 text-sm font-semibold text-foreground-950">{award.name}</h4>
                    <p className="mt-1 text-xs text-foreground-500">{award.year}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-12 md:py-18 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <h2 className="text-xl md:text-3xl font-bold text-foreground-950 mb-6">
                    联系我们
                  </h2>
                  <div className="flex flex-col gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-map-pin-line"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-950">公司地址</h4>
                        <p className="text-sm text-foreground-600">广东省深圳市南山区科技园南路 88 号 MJHL 大厦</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-phone-line"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-950">客服热线</h4>
                        <p className="text-sm text-foreground-600">400-888-9999（7x24 小时）</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-mail-line"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-950">电子邮箱</h4>
                        <p className="text-sm text-foreground-600">contact@mjhl.com</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="text-xl md:text-3xl font-bold text-foreground-950 mb-6">
                    留言咨询
                  </h2>
                  <form
                    id="contact-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-4"
                  >
                    <div>
                      <label htmlFor="contact-name" className="text-sm font-medium text-foreground-700">
                        姓名 <span className="text-primary-600">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="mt-1 w-full px-3 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="text-sm font-medium text-foreground-700">
                        邮箱 <span className="text-primary-600">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="mt-1 w-full px-3 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-company" className="text-sm font-medium text-foreground-700">
                        公司
                      </label>
                      <input
                        id="contact-company"
                        name="company"
                        type="text"
                        className="mt-1 w-full px-3 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的公司名称（选填）"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="text-sm font-medium text-foreground-700">
                        留言内容 <span className="text-primary-600">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="mt-1 w-full px-3 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                    </div>
                    <div className="honeypot-field">
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>
                    {formStatus === 'error' && formError && (
                      <p className="text-sm text-red-600">{formError}</p>
                    )}
                    {formStatus === 'success' && successMessage && (
                      <p className="text-sm text-green-600">{successMessage}</p>
                    )}
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full px-6 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 disabled:opacity-50 transition-all duration-200 cursor-pointer whitespace-nowrap"
                    >
                      {formStatus === 'loading' ? '提交中...' : '提交留言'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-12 md:py-18 bg-background-50">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-xl md:text-3xl font-bold text-foreground-950 text-center mb-10">
                常见问题
              </h2>
              <div className="max-w-3xl mx-auto flex flex-col gap-3">
                {faqs.map((faq) => (
                  <details
                    key={faq.id}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden"
                  >
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                      <h4 className="text-sm font-semibold text-foreground-950 pr-4">
                        {faq.question}
                      </h4>
                      <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform duration-200 flex-shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}